import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const API_BASE = () =>
  (import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api';

async function chamar<T>(caminho: string, init?: RequestInit): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${API_BASE()}/v1/mcp/admin${caminho}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${session?.access_token ?? ''}`,
      ...(init?.headers ?? {}),
    },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error ?? `Falhou com HTTP ${res.status}`);
  return (json?.data ?? json) as T;
}

export interface McpPessoa {
  id: string;
  nome: string;
  perfil: string;
  ativo: boolean;
  /** O usuário do CRM dono do token — é dele a visão nas consultas que seguem a tela. */
  usuario_id: string | null;
  usuario_nome: string | null;
  equipe: string | null;
  perguntas: number;
  ultimo_uso: string | null;
  criado_em: string;
}

export interface McpFerramenta {
  slug: string;
  nome: string;
  dominio: string;
  ativo: boolean;
  usos: number;
  ultimo_uso: string | null;
}

export interface McpConsulta {
  id: string;
  pessoa_nome: string | null;
  ferramenta: string | null;
  parametros: Record<string, unknown> | null;
  linhas: number | null;
  duracao_ms: number | null;
  erro: string | null;
  criado_em: string;
}

export interface McpGap {
  id: string;
  assunto: string;
  /** Quantas vezes o mesmo recado foi reapresentado enquanto estava aberto. */
  pedidos: number;
  desde: string;
  ultimo: string;
  pediram: string | null;
  exemplo: string;
  /** pessoa = alguém pediu · ferramenta = faltou ferramenta · sistema = o CRM falhou e se anunciou */
  origem?: 'pessoa' | 'ferramenta' | 'sistema';
  prioridade?: 'baixa' | 'media' | 'alta';
  /** A base técnica do recado (25/09/2026). Recados antigos vêm sem ela. */
  tipo?: 'falha' | 'dado_errado' | 'melhoria' | 'duvida' | 'reuniao' | null;
  tela?: string | null;
  aconteceu?: string | null;
  esperado?: string | null;
  evidencia?: string | null;
  impacto?: string | null;
  contexto?: string | null;
  /** As consultas que a pessoa rodou na hora anterior ao recado — a prova. */
  consultas_recentes?: Array<{ quando: string; ferramenta: string; parametros: unknown; linhas: number | null; erro: string | null }> | null;
}

/** Item da pauta da reunião mensal — recado tipo `reuniao`, lido pelo Manus em apres_adicionais. */
export interface McpItemReuniao {
  id: string;
  assunto: string;
  pedido: string;
  contexto: string | null;
  pessoa_nome: string;
  status: 'aberto' | 'resolvido' | 'descartado';
  resposta: string | null;
  /** AAAA-MM-DD */
  reuniao_data: string | null;
  criado_em: string;
  fechado_em: string | null;
}

export interface McpResumo {
  pessoas: McpPessoa[];
  ferramentas: McpFerramenta[];
  consultas: McpConsulta[];
  gaps: McpGap[];
  usuarios: Array<{ id: string; nome: string; equipe: string | null }>;
  reuniao: McpItemReuniao[];
}

export function useMcpResumo() {
  return useQuery<McpResumo>({
    queryKey: ['mcp', 'resumo'],
    queryFn: () => chamar<McpResumo>('/resumo'),
    staleTime: 30_000,
  });
}

export function useCriarPessoa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { nome: string; perfil: string; usuario_id?: string | null }) =>
      chamar<{ id: string; nome: string; perfil: string; token: string }>('/pessoas', {
        method: 'POST',
        body: JSON.stringify(dados),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp', 'resumo'] }),
  });
}

export function useAjustarPessoa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dados }: { id: string; ativo?: boolean; perfil?: string; novo_token?: boolean; usuario_id?: string | null }) =>
      chamar<{ id: string; token?: string }>(`/pessoas/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(dados),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp', 'resumo'] }),
  });
}

export function useResolverGap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dados }: { id: string; resposta?: string; descartar?: boolean; tool_criada_slug?: string }) =>
      chamar<{ id: string }>(`/gaps/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(dados),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp', 'resumo'] }),
  });
}

/**
 * O AVISO SIMPLES — quantos recados esperam resposta.
 *
 * Douglas, 22/09/2026: "um aviso simples quando tiver recado seria importante".
 * É o contador da sidebar, no MCP Prime: só o número, do mesmo jeito que o
 * WhatsApp Inbox e os Avisos já fazem. Nada de popup — os 48 pedidos da LETICIA
 * ficaram invisíveis por seis dias, e o que faltava era um número na tela, não
 * um alerta no meio do trabalho.
 */
export function useRecadosAbertos(ligado: boolean) {
  return useQuery({
    queryKey: ['mcp', 'recados', 'contador'],
    enabled: ligado,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    queryFn: () => chamar<{ abertos: number; falhas: number }>('/recados/contador'),
  });
}

export function useIncluirItemReuniao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { assunto: string; conteudo: string; reuniao_data: string }) =>
      chamar<{ id: string }>('/reuniao', {
        method: 'POST',
        body: JSON.stringify(dados),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp', 'resumo'] }),
  });
}
